import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import QuickQuote from "@/components/QuickQuote";
import { thumb } from "@/lib/img";

export const SeoHero = ({ eyebrow, title, accent, intro, chips = [] }) => (
  <section className="pt-[120px] pb-12">
    <div className="shg-container">
      <div className="shg-eyebrow mb-5">{eyebrow}</div>
      <h1 data-testid="seo-h1" className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[0.95] mb-6 max-w-4xl">
        {title} {accent && <span style={{ color: "var(--red)" }}>{accent}</span>}
      </h1>
      <p className="text-lg text-[var(--text-soft)] max-w-3xl leading-relaxed">{intro}</p>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-6">
          {chips.map((c) => <span key={c} className="px-3 py-1.5 rounded-full text-xs border" style={{ borderColor: "var(--line-strong)", color: "var(--text-soft)" }}>{c}</span>)}
        </div>
      )}
    </div>
  </section>
);

export const SeoSection = ({ title, children, testId }) => (
  <section data-testid={testId} className="py-10">
    <div className="shg-container">
      <h2 className="font-display text-3xl md:text-4xl mb-5">{title}</h2>
      {children}
    </div>
  </section>
);

export const CheckList = ({ items }) => (
  <ul className="grid sm:grid-cols-2 gap-3">
    {items.map((b) => <li key={b} className="flex gap-3 items-start"><Check size={18} className="text-[var(--red)] mt-1 flex-shrink-0" /><span className="text-[var(--text-soft)]">{b}</span></li>)}
  </ul>
);

export const FaqList = ({ faqs }) => (
  <div>
    {faqs.map((f) => (
      <details key={f.q} className="mb-3 rounded-xl p-5" style={{ background: "var(--surface)", border: "1px solid var(--line)" }}>
        <summary className="cursor-pointer font-medium">{f.q}</summary>
        <p className="text-[var(--text-soft)] text-sm leading-relaxed mt-3">{f.a}</p>
      </details>
    ))}
  </div>
);

export const ExpoRefs = ({ expos, title = "Project references" }) => (
  expos.length ? (
    <SeoSection title={title} testId="expo-refs">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {expos.map((e) => (
          <Link key={e.slug} to={`/expo/${e.slug}`} data-testid={`ref-${e.slug}`} className="shg-card p-4 flex items-center gap-4 group">
            <img src={thumb(e.heroImage)} alt={`${e.name} stand in ${e.city}`} loading="lazy" decoding="async" className="w-20 h-14 object-cover rounded-md" />
            <div className="min-w-0"><div className="font-display text-base leading-tight group-hover:text-[var(--red)] transition-colors truncate">{e.name}</div><div className="text-xs text-[var(--text-muted)]">{e.city} · {e.clients.length} {e.clients.length === 1 ? "stand" : "stands"}</div></div>
            <ArrowUpRight size={14} className="ml-auto text-[var(--red)] flex-shrink-0" />
          </Link>
        ))}
      </div>
    </SeoSection>
  ) : null
);

export const LinkGrid = ({ items, base, testPrefix }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
    {items.map((i) => (
      <Link key={i.slug} to={`${base}/${i.slug}`} data-testid={`${testPrefix}-${i.slug}`} className="shg-card p-5 group flex items-start justify-between gap-3">
        <div><div className="font-display text-lg leading-tight group-hover:text-[var(--red)] transition-colors">{i.title}</div>{i.sub && <div className="text-xs text-[var(--text-muted)] mt-1">{i.sub}</div>}</div>
        <ArrowUpRight size={15} className="text-[var(--red)] flex-shrink-0 mt-1" />
      </Link>
    ))}
  </div>
);

export const QuoteBand = ({ expoName, source }) => (
  <section className="py-12"><div className="shg-container max-w-3xl"><QuickQuote expoName={expoName} source={source} /></div></section>
);
